import { Box, Button, Stack } from "@mui/material";
import { AskContainer, FeedbackButton } from "../Components";
import { useChatWindow } from "./Hooks";
import { InfoHeader, MobileReferenceDrawer, References } from "./Containers";
import "./markdown.css";
import { ChatTextField, DataGridLoading, Snackbar, ChatLoadingAnimation } from "Common/Components";
import { AskAMIBotIcon, ThumbsDownOutlined, ThumbsUpOutlined } from "Icons/Icons";
import { InitialChat } from "../InitialChat";
import { USER_MESSAGE_TYPES } from "Common/Constants/UserMessageTypes";
import FeedbackPopup from "../Components/FeedbackPopup";
import MarkdownRenderer from "../Components/MarkdownRenderer";
import { IChatWindow } from "./Interfaces";
import { UI_ELEMENTS } from "Common/Constants";
import { CUSTOM_RADII } from "Theme/Shape";
import ThumbUpOffAltRoundedIcon from "@mui/icons-material/ThumbUpOffAltRounded";
import ThumbDownRoundedIcon from "@mui/icons-material/ThumbDownRounded";
import React, { useContext } from "react";
import { AuthContext } from "Layout/MainLayout";

export const ChatWindow = (props: IChatWindow) => {
  const {
    theme,
    conversation,
    editingMessage,
    hoveredMessageKey,
    handleSaveEdit,
    handleCancelEdit,
    setHoveredMessageKey,
    isConversationMessagesLoading,
    isConversationMessagesFetching,
    isConversationLoading,
    handleLinkClick,
    handlePositiveFeedback,
    handleNegativeFeedback,
    setIsCollapsedReference,
    isCollapsedReference,
    getRequestMessage,
    activeUrl,
    pageNum,
    resetReference,
    paragraphs,
    isDialogOpen,
    setIsDialogOpen,
    handleSubmitComment,
    matchDownMd,
    referenceDrawer,
    setReferenceDrawer,
    uiElementState,
    handleCloseUIElement,
    selectedFeedback,
    showFeedbackButtons,
    pageResetKey,
    isConversationProcessing,
    handleOpenSnackbar,
    infoType,
    setInfoType,
    divisionScope,
    isDivisionLoading,
    scrollContainerRef,
  } = useChatWindow(props);

  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("RenderConversation must be used within an AuthProvider");
  }
  const { isLoadingAuthorization } = context;

  const renderConversation = () => {
    if (conversation === undefined && !isConversationMessagesFetching && !isLoadingAuthorization) {
      return <InitialChat sendRequestMessage={getRequestMessage} infoType={infoType} setInfoType={setInfoType} />;
    }

    if (conversation?.messages?.length) {
      const conversationMessages = conversation.messages
        .filter((message) => message.user !== USER_MESSAGE_TYPES.SYSTEM)
        .map((message) => (
          <Box
            key={message.key}
            sx={{
              display: "flex",
              justifyContent: message.user === USER_MESSAGE_TYPES.USER ? "flex-end" : "flex-start",
              width: "100%",
            }}
            onMouseEnter={() => setHoveredMessageKey(message.key)}
            onMouseLeave={() => setHoveredMessageKey(null)}
          >
            <Box
              sx={{
                maxWidth: 650,
                width: "auto",
                padding: 1,
                position: "relative",
                cursor: message.user === USER_MESSAGE_TYPES.USER ? "pointer" : "default",
              }}
            >
              {editingMessage === message.key ? (
                <Box
                  sx={{
                    backgroundColor: message.user === USER_MESSAGE_TYPES.USER ? theme.palette.grey[200] : "#fff",
                    minWidth: 400,
                    width: "auto",
                    p: 1,
                  }}
                >
                  <ChatTextField />
                  <Stack
                    direction={"row"}
                    spacing={1}
                    sx={{ display: "flex", alignItems: "end", justifyContent: "flex-end" }}
                  >
                    <Button
                      onClick={handleCancelEdit}
                      variant="outlined"
                      sx={{
                        color: theme.palette.grey[700],
                        minWidth: 90,
                        border: theme.palette.grey[700],
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => handleSaveEdit(message.key)}
                      variant="contained"
                      sx={{
                        background: theme.palette.primary.dark,
                        minWidth: 90,
                      }}
                    >
                      Send
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ maxWidth: 650, flexShrink: 1 }}>
                  <Stack
                    sx={{
                      mx: "auto",
                      justifyContent: message.user === USER_MESSAGE_TYPES.USER ? "flex-end" : "flex-start",
                      maxWidth: 650,
                      display: "flex",
                      alignItems: "start",
                    }}
                    direction={"row"}
                    spacing={2}
                  >
                    {message.user !== USER_MESSAGE_TYPES.USER && message.user !== USER_MESSAGE_TYPES.ERROR && (
                      <Box sx={{ pt: 2 }}>
                        <AskAMIBotIcon sx={{ width: "36px", height: "36px" }} />
                      </Box>
                    )}
                    <Box
                      sx={{
                        maxWidth: "650px",
                        p: message.user === USER_MESSAGE_TYPES.USER ? 2 : 0,
                        borderRadius: CUSTOM_RADII.card,
                        backgroundColor: message.user === USER_MESSAGE_TYPES.USER ? theme.palette.grey[200] : "#fff",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        color: message.user === USER_MESSAGE_TYPES.ERROR ? theme.palette.error.main : "inherit",
                      }}
                    >
                      {message.user === USER_MESSAGE_TYPES.USER ? (
                        <Box>{message.text}</Box>
                      ) : (
                        <MarkdownRenderer message={{ text: message.text }} handleLinkClick={handleLinkClick} />
                      )}
                    </Box>
                  </Stack>
                  {showFeedbackButtons &&
                    message.user !== USER_MESSAGE_TYPES.USER &&
                    message.user !== USER_MESSAGE_TYPES.ERROR && (
                      <Stack
                        direction={"row"}
                        spacing={1}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          mt: 1,
                          pt: 1,
                          borderTop:
                            hoveredMessageKey === message.key
                              ? `1px solid ${theme.palette.grey[300]}`
                              : "1px solid transparent",
                        }}
                      >
                        {!message.feedbacks || message.feedbacks?.length === 0 ? (
                          <>
                            {selectedFeedback.every(
                              (feedback) => feedback.messageId !== message.key || feedback.type !== "down",
                            ) && (
                              <FeedbackButton
                                tooltipTitle="Good response"
                                visibility={hoveredMessageKey !== message.key}
                                primary
                                Icon={
                                  selectedFeedback.some(
                                    (feedback) => feedback.messageId === message.key && feedback.type === "up",
                                  )
                                    ? ThumbUpOffAltRoundedIcon
                                    : ThumbsUpOutlined
                                }
                                onClick={() => {
                                  handlePositiveFeedback(conversation?.conversationId ?? "", message.key, true, undefined);
                                }}
                                disabled={
                                  hoveredMessageKey === message.key &&
                                  selectedFeedback.some(
                                    (feedback) => feedback.messageId === message.key && feedback.type === "up",
                                  )
                                }
                              />
                            )}

                            {selectedFeedback.every(
                              (feedback) => feedback.messageId !== message.key || feedback.type !== "up",
                            ) && (
                              <FeedbackButton
                                tooltipTitle="Bad response"
                                visibility={hoveredMessageKey !== message.key}
                                Icon={
                                  selectedFeedback.some(
                                    (feedback) => feedback.messageId === message.key && feedback.type === "down",
                                  )
                                    ? ThumbDownRoundedIcon
                                    : ThumbsDownOutlined
                                }
                                onClick={() => {
                                  handleNegativeFeedback(conversation?.conversationId ?? "", message.key, false);
                                }}
                                disabled={
                                  hoveredMessageKey === message.key &&
                                  selectedFeedback.some(
                                    (feedback) => feedback.messageId === message.key && feedback.type === "down",
                                  )
                                }
                              />
                            )}
                          </>
                        ) : (
                          message.feedbacks.map((feedback) =>
                            feedback.thumbs === true ? (
                              <FeedbackButton
                                key={feedback.feedbackId}
                                tooltipTitle="Good response"
                                visibility={hoveredMessageKey !== message.key}
                                Icon={ThumbUpOffAltRoundedIcon}
                                primary
                                disabled
                              />
                            ) : (
                              <FeedbackButton
                                key={feedback.feedbackId}
                                tooltipTitle="Bad response"
                                visibility={hoveredMessageKey !== message.key}
                                Icon={ThumbDownRoundedIcon}
                                disabled
                              />
                            ),
                          )
                        )}
                      </Stack>
                    )}
                </Box>
              )}
            </Box>
          </Box>
        ));

      // Add loading animation at the end if conversation is loading
      // Only show if there are no messages or the last message is from the user
      // Also show if the last bot message is empty or very short (likely streaming hasn't started yet)
      if (isConversationLoading) {
        const lastMessage = conversation.messages[conversation.messages.length - 1];
        const isLastMessageFromUser = lastMessage?.user === USER_MESSAGE_TYPES.USER;
        const isLastMessageEmptyBot =
          lastMessage?.user !== USER_MESSAGE_TYPES.USER &&
          lastMessage?.user !== USER_MESSAGE_TYPES.ERROR &&
          (!lastMessage?.text || lastMessage.text.trim().length < 1);
        const shouldShowLoadingAnimation = !lastMessage || isLastMessageFromUser || isLastMessageEmptyBot;

        if (shouldShowLoadingAnimation) {
          // If the last message is an empty bot message, exclude it from rendering
          // to avoid duplicate bot icons (one in the empty message, one in loading animation)
          const messagesToRender = isLastMessageEmptyBot ? conversationMessages.slice(0, -1) : conversationMessages;

          return (
            <>
              {messagesToRender}
              <ChatLoadingAnimation />
            </>
          );
        }
      }

      return conversationMessages;
    }

    return null;
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          width: "100%",
          height: "100dvh",
          overflow: "hidden",
        }}
      >
        {conversation && (
          <InfoHeader
            infoType={infoType}
            isDivisionLoading={isDivisionLoading}
            isConversationLoading={isConversationLoading}
            divisionScope={divisionScope ?? ""}
            setIsCollapsedReference={setIsCollapsedReference}
            setReferenceDrawer={setReferenceDrawer}
          />
        )}

        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "calc(100dvh - 100px)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: isCollapsedReference ? "0 0 100%" : "0 0 50%",
              transition: "flex 0.3s ease-in-out",
              display: "flex",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <DataGridLoading
                sx={{ p: 3 }}
                isLoading={
                  // Show loading when:
                  // 1. isConversationProcessing - actively switching to a different chat
                  // 2. No messages AND fetching - initial load
                  // Prevents flash during background refetch for divisionScope updates
                  isConversationProcessing ||
                  (!conversation?.messages?.length && (isConversationMessagesLoading || isConversationMessagesFetching))
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    width: "100%",
                  }}
                >
                  <Box
                    ref={scrollContainerRef}
                    sx={{
                      flex: "1 1 auto",
                      overflowY: "auto",
                      minHeight: 0,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: 1000,
                        width: "100%",
                        pt: 1,
                        px: 2,
                      }}
                    >
                      {renderConversation()}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flex: "0 0 auto",
                      backgroundColor: "white",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Box sx={{ maxWidth: 1000, width: "100%", py: 2 }}>
                      {conversation !== undefined && (
                        <AskContainer sendRequestMessage={getRequestMessage} openSnackBar={handleOpenSnackbar} />
                      )}
                    </Box>
                  </Box>
                </Box>
              </DataGridLoading>
            </Box>
          </Box>

          <Box
            sx={{
              flex: "0 0 50%",
              transform: isCollapsedReference ? "translateX(100%)" : "translateX(0)",
              transition: "transform 0.3s ease-in-out",
              height: "100%",
            }}
          >
            {!matchDownMd && (
              <References
                isCollapsedReference={isCollapsedReference}
                setIsCollapsedReference={setIsCollapsedReference}
                conversationId={conversation?.conversationId}
                activeReferenceUrl={activeUrl}
                pageNumber={pageNum}
                isConversationLoading={isConversationLoading}
                resetReference={resetReference}
                paragraphs={paragraphs}
                pageResetKey={pageResetKey}
                handleDocumentView={handleLinkClick}
              />
            )}
          </Box>
        </Box>
      </Box>

      <MobileReferenceDrawer
        referenceDrawer={referenceDrawer}
        setReferenceDrawer={setReferenceDrawer}
        conversationId={conversation?.conversationId}
        activeReferenceUrl={activeUrl}
        pageNumber={pageNum}
        isConversationLoading={isConversationLoading}
        resetReference={resetReference}
        paragraphs={paragraphs}
        pageResetKey={pageResetKey}
        handleDocumentView={handleLinkClick}
      />

      <FeedbackPopup open={isDialogOpen} onClose={() => setIsDialogOpen(false)} onSubmit={handleSubmitComment} />

      <Snackbar
        open={uiElementState.activeElement === UI_ELEMENTS.SNACKBAR}
        handleClose={handleCloseUIElement}
        message={uiElementState.snackbarMessage}
        severity={uiElementState.snackbarSeverity}
      />
    </React.Fragment>
  );
};
